import type { Permission } from '@canopee/contracts';

export interface NavigationItem {
  readonly label: string;
  readonly path: string;
  readonly permission: Permission;
  readonly group: 'Général' | 'Réservation' | 'Finance' | 'Compte' | 'Administration';
}

const declaredNavigationItems = [
  {
    label: 'EPI',
    path: '/epi',
    permission: 'epi:read',
    group: 'Général',
  },
  {
    label: 'Logistique',
    path: '/logistics',
    permission: 'logistics:cold:read',
    group: 'Général',
  },
  {
    label: 'Ressources',
    path: '/resources',
    permission: 'resources:catalog:read',
    group: 'Général',
  },
  {
    label: 'Matériel groupe',
    path: '/stock/group',
    permission: 'stock:read',
    group: 'Général',
  },
  {
    label: 'Matériel camp',
    path: '/stock/camp',
    permission: 'stock:read',
    group: 'Général',
  },
  {
    label: 'Tableau de bord',
    path: '/',
    permission: 'reservations:read',
    group: 'Général',
  },
  {
    label: 'Planning séjours',
    path: '/planning',
    permission: 'reservations:read',
    group: 'Réservation',
  },
  {
    label: 'Séjours',
    path: '/stays',
    permission: 'reservations:read',
    group: 'Réservation',
  },
  {
    label: 'Réservations',
    path: '/reservations',
    permission: 'reservations:read',
    group: 'Réservation',
  },
  {
    label: 'Devis / Options',
    path: '/quotes',
    permission: 'reservations:read',
    group: 'Réservation',
  },
  {
    label: 'Clients',
    path: '/customers',
    permission: 'customers:read',
    group: 'Réservation',
  },
  {
    label: 'Débiteurs',
    path: '/debtors',
    permission: 'finance:read',
    group: 'Finance',
  },
  {
    label: 'Avoirs',
    path: '/credits',
    permission: 'finance:write',
    group: 'Finance',
  },
  {
    label: 'Clôture Finance',
    path: '/finance',
    permission: 'finance:write',
    group: 'Finance',
  },
  {
    label: 'Bons cadeaux',
    path: '/gift-vouchers',
    permission: 'reservations:read',
    group: 'Finance',
  },
  {
    label: 'Codes promo',
    path: '/promo-codes',
    permission: 'reservations:read',
    group: 'Finance',
  },
  {
    label: 'Mon profil',
    path: '/profile',
    permission: 'profile:read',
    group: 'Compte',
  },
  {
    label: 'Utilisateurs',
    path: '/admin/users',
    permission: 'users:read',
    group: 'Administration',
  },
] as const satisfies readonly NavigationItem[];

function validateNavigationItems<TItems extends readonly NavigationItem[]>(items: TItems): TItems {
  const paths = new Map<string, string>();
  const labels = new Map<string, string>();
  for (const item of items) {
    if (!item.path.startsWith('/') || (item.path.length > 1 && item.path.endsWith('/'))) {
      throw new Error(`Route de navigation non canonique : ${item.path}`);
    }
    const previousPathLabel = paths.get(item.path);
    if (previousPathLabel !== undefined) {
      throw new Error(
        `Entrée de navigation dupliquée pour ${item.path} : ${previousPathLabel} / ${item.label}`,
      );
    }
    const previousLabelPath = labels.get(item.label);
    if (previousLabelPath !== undefined) {
      throw new Error(
        `Libellé de navigation dupliqué pour ${item.label} : ${previousLabelPath} / ${item.path}`,
      );
    }
    paths.set(item.path, item.label);
    labels.set(item.label, item.path);
  }
  return items;
}

export const navigationItems = validateNavigationItems(declaredNavigationItems);

export function navigationItemsForPermissions(
  permissions: readonly Permission[],
): readonly NavigationItem[] {
  return navigationItems.filter((item) => permissions.includes(item.permission));
}

export function navigationGroupsForItems(
  items: readonly NavigationItem[],
): readonly NavigationItem['group'][] {
  return items.reduce<NavigationItem['group'][]>(
    (groups, item) => (groups.includes(item.group) ? groups : [...groups, item.group]),
    [],
  );
}

export function navigationItemIsActive(item: NavigationItem, pathname: string): boolean {
  if (item.path === '/') return pathname === '/';
  return pathname === item.path || pathname.startsWith(`${item.path}/`);
}

export const NAVIGATION_AUTHORITY = {
  rendererId: 'canopee-primary-navigation',
  duplicateEntryPolicy: 'reject-at-module-load',
  activePathPolicy: 'exact-root-and-section-prefix',
  items: navigationItems,
} as const;
